import { servicePath } from './projectOverview';
import { previewStateOf } from './previewReadiness';

/**
 * The scope of a service-scoped tab: Home (the project), the project's files, or one service. The selector must always
 * say what the open file belongs to: a file under `services/app/` is the service `app`, whatever opened the Files view.
 */

/** The service key whose folder holds `path` (the longest match), or `null` for a file outside every service. */
export function serviceOfPath(path, serviceKeys) {
  const clean = String(path || '').replace(/^\.?\/+/, '').replace(/\/+$/, '');
  if (!clean) { return null; }
  let best = null;
  let bestLength = -1;
  for (const key of serviceKeys || []) {
    const folder = servicePath(key);
    if (folder === '.') { continue; }
    if ((clean === folder || clean.startsWith(`${folder}/`)) && folder.length > bestLength) {
      best = key;
      bestLength = folder.length;
    }
  }
  return best;
}

/**
 * What the Files view at Home is about: the service of the open file, else of the folder it is rooted at, else `null`
 * (the project root, files outside any service).
 */
export function filesScopeOf({ focus, openFile, serviceKeys }) {
  return serviceOfPath(openFile, serviceKeys) || serviceOfPath(focus, serviceKeys);
}

/** The dot of a service in the selector: `ready`, `starting`, `stopped` or `failed`; `null` when it is not known. */
export function scopeDotState(services, serviceName, known = true) {
  const state = previewStateOf(services, serviceName, known);
  if (state === null) { return null; }
  if (state === 'ready') { return 'ready'; }
  if (state === 'starting') { return 'starting'; }
  if (state === 'crashed' || state === 'failed') { return 'failed'; }
  return 'stopped';
}

const KEY = 'flow.lastService.';

/** The service this person last picked in the project (a path key), or `null`. */
export function lastServiceOf(projectId) {
  if (!projectId) { return null; }
  try { return window.localStorage.getItem(`${KEY}${projectId}`) || null; } catch { return null; }
}

export function rememberService(projectId, serviceName) {
  if (!projectId || !serviceName) { return; }
  try { window.localStorage.setItem(`${KEY}${projectId}`, serviceName); } catch { /* storage blocked: nothing remembered */ }
}

/** The service to open from Home: the last picked one if the project still has it, else the first. */
export function preferredService(projectId, serviceValues) {
  const last = lastServiceOf(projectId);
  return last && (serviceValues || []).includes(last) ? last : (serviceValues || [])[0] || null;
}
